import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useFlash } from 'contexts/flash';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { updateAssetIssues } from 'services/locations';
import { loadAssetIssueById, WorkflowIssue } from 'services/report';
import { buildIssueAssignmentPutFields } from 'utils/issueAssignmentPutFields';
import { getIssuesConfig } from 'services/option';
import { DEFAULT_ISSUES_UI_CONFIG, IssuesUiConfig } from 'utils/issueDueDateColor';
import { DEFAULT_CRITICALITY_LEVELS } from 'utils/issueCriticality';
import { FormDetailReportSchema } from 'utils/validate';
import { appendDynamicOptionForPayload, appendDynamicSingleForPayload } from 'utils/convertData';
import { dropdownValueTypes } from 'const/option';
import { entriesInclude } from 'utils/entries';
import { getApiErrorMessage } from 'utils/apiErrorMessage';
import type { IssueAssignmentFormValues } from '../components/IssueAssignmentFields';

export type AssetIssueFormValues = {
  equipmentId: string;
  name: string;
  description?: string;
  status?: string;
  note?: string | null;
  actionBy?: string | null;
  actionByName?: string;
  actionByDate?: string | null;
  resolutionDate?: string;
  [key: string]: unknown;
};

type PhotoRow = {
  id: number;
  url: string;
  storage: { fileName: string; type: string };
};

export const useAssetIssueDialog = ({
  isOpen,
  issueId,
  onSaved
}: {
  isOpen: boolean;
  issueId?: number;
  onSaved?: () => void;
}) => {
  const { setFlash } = useFlash();
  const { systemDropdownValues, singleEntryField } = useFilteredRoomContext();
  const [loading, setLoading] = useState(false);
  const [issue, setIssue] = useState<WorkflowIssue | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);
  const [newBeforePhotos, setNewBeforePhotos] = useState<File[]>([]);
  const [newAfterPhotos, setNewAfterPhotos] = useState<File[]>([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState<number[]>([]);

  const legacyForm = useForm<AssetIssueFormValues>({
    resolver: yupResolver(FormDetailReportSchema)
  });

  const assignmentForm = useForm<IssueAssignmentFormValues>({
    defaultValues: {
      assignedToUserId: '',
      dueDate: null,
      status: 'ACTIVE',
      criticalityKey: ''
    }
  });

  const criticalityLevels = issuesConfig.criticalityLevels?.length
    ? issuesConfig.criticalityLevels
    : DEFAULT_CRITICALITY_LEVELS;

  const submitId = issue?.submitId ?? issue?.submittedAsset?.submitId;
  const submittedAssetId = issue?.submittedAssetId ?? issue?.submittedAsset?.id;

  const reportIssueDropdownOption = useMemo(
    () =>
      systemDropdownValues?.filter((item) =>
        item.options.some((option) => option.type === dropdownValueTypes.reportIssue)
      ) ?? [],
    [systemDropdownValues]
  );

  const reportIssueSingleFields = useMemo(
    () =>
      singleEntryField?.filter((item) =>
        entriesInclude(item.entries, dropdownValueTypes.reportIssue)
      ) ?? [],
    [singleEntryField]
  );

  const loadIssue = useCallback(async () => {
    if (!issueId) return;
    setLoading(true);
    try {
      const data = await loadAssetIssueById(issueId);
      const loaded = data.issue;
      setIssue(loaded);
      setNewBeforePhotos([]);
      setNewAfterPhotos([]);
      setDeletedPhotoIds([]);

      const reportIssueOptions: Record<string, unknown> = {};
      systemDropdownValues?.forEach((dropdownValue) => {
        reportIssueOptions[`option_${dropdownValue.id}`] = '';
        reportIssueOptions[`option_${dropdownValue.id}_name`] = '';
      });
      singleEntryField?.forEach((field) => {
        reportIssueOptions[`single_${field.id}`] = '';
      });

      loaded.options?.forEach((issueOption) => {
        if (issueOption.fieldType === 'DROPDOWN') {
          systemDropdownValues?.forEach((dropdownValue) => {
            if (dropdownValue.id.toString() === issueOption.type) {
              const index = dropdownValue.dropdownValues.some(
                (dropdown) => dropdown.value === issueOption.value
              );
              reportIssueOptions[`option_${issueOption.type}`] = index
                ? issueOption.value
                : 'OTHER';
              reportIssueOptions[`option_${issueOption.type}_name`] = issueOption.value;
            }
          });
        } else {
          reportIssueOptions[`single_${issueOption.type}`] = issueOption.value;
        }
      });

      legacyForm.reset({
        description: loaded.description,
        equipmentId: loaded.equipmentId || '',
        actionBy: !loaded.actionBy?.startsWith('other_') ? loaded.actionBy : 'OTHER',
        actionByName: !loaded.actionBy?.startsWith('other_')
          ? ''
          : loaded.actionBy?.replace('other_', ''),
        actionByDate: loaded.actionByDate,
        resolutionDate: loaded.resolutionDate as string | undefined,
        status: loaded.status,
        note: loaded.note,
        name: loaded.name || '',
        ...reportIssueOptions
      });

      assignmentForm.reset({
        assignedToUserId: loaded.assignedToUser?.id ? String(loaded.assignedToUser.id) : '',
        dueDate: loaded.dueDate ? dayjs(loaded.dueDate) : null,
        status: loaded.status || 'ACTIVE',
        criticalityKey: loaded.criticalityKey || loaded.criticality?.key || ''
      });
    } catch (err) {
      setFlash({ type: 'error', message: getApiErrorMessage(err, 'Failed to load asset issue.') });
    } finally {
      setLoading(false);
    }
  }, [issueId, legacyForm, assignmentForm, setFlash, singleEntryField, systemDropdownValues]);

  useEffect(() => {
    getIssuesConfig()
      .then((res) => setIssuesConfig({ ...DEFAULT_ISSUES_UI_CONFIG, ...(res?.config || {}) }))
      .catch(() => setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG));
  }, []);

  useEffect(() => {
    if (!isOpen || !issueId) return;
    setIsEditing(false);
    loadIssue();
  }, [isOpen, issueId, loadIssue]);

  const mapPhotosForManagement = useCallback(
    (photos: WorkflowIssue['issuePhotosBefore']): PhotoRow[] =>
      (photos || [])
        .filter((p) => !deletedPhotoIds.includes(Number(p.id)))
        .map((p) => ({
          id: Number(p.id),
          url: p.url || p.photo?.url || '',
          storage: {
            fileName: p.photo?.fileName || `Photo ${p.id}`,
            type: 'image'
          }
        })),
    [deletedPhotoIds]
  );

  const existingBeforePhotos = useMemo(
    () =>
      mapPhotosForManagement(
        issue?.issuePhotosBefore?.length
          ? issue.issuePhotosBefore
          : issue?.issueReportPhotos?.filter((p) => !p.photoType || p.photoType === 'ISSUE_BEFORE')
      ),
    [issue, mapPhotosForManagement]
  );

  const existingAfterPhotos = useMemo(
    () => mapPhotosForManagement(issue?.issuePhotosAfter),
    [issue?.issuePhotosAfter, mapPhotosForManagement]
  );

  const handleSave = legacyForm.handleSubmit(async (valueForm) => {
    if (!issueId) return;
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    const resolvedSubmitId = issue?.submitId ?? issue?.submittedAsset?.submitId;
    const resolvedSubmittedAssetId = issue?.submittedAssetId ?? issue?.submittedAsset?.id;

    if (!resolvedSubmitId || !resolvedSubmittedAssetId) {
      setFlash({ type: 'error', message: 'Missing issue context. Please reload.' });
      return;
    }

    try {
      const assignmentValues = assignmentForm.getValues();
      const allNewFiles = [...newBeforePhotos, ...newAfterPhotos];
      const photoTypes = [
        ...newBeforePhotos.map(() => 'ISSUE_BEFORE'),
        ...newAfterPhotos.map(() => 'ISSUE_AFTER')
      ];

      const assignmentPutFields = buildIssueAssignmentPutFields(assignmentValues);
      const workflowStatus = assignmentPutFields.status || valueForm.status;

      await updateAssetIssues({
        issuesId: issueId,
        assetId: Number(resolvedSubmittedAssetId),
        submitId: Number(resolvedSubmitId),
        payload: {
          name: valueForm.name,
          actionBy:
            valueForm.actionBy === 'OTHER'
              ? `other_${valueForm.actionByName}`
              : valueForm.actionBy || '',
          actionByDate: valueForm.actionByDate || '',
          resolutionDate: workflowStatus === 'ACTIVE' ? null : valueForm.resolutionDate,
          equipmentId: valueForm.equipmentId,
          description: valueForm.description,
          status: workflowStatus != null ? String(workflowStatus) : undefined,
          note: valueForm.note,
          ...assignmentPutFields,
          options: JSON.stringify(appendDynamicOptionForPayload(valueForm)),
          singleFields: JSON.stringify(appendDynamicSingleForPayload(valueForm)),
          deletePhotoIds: deletedPhotoIds.length > 0 ? JSON.stringify(deletedPhotoIds) : undefined,
          photoTypes: allNewFiles.length ? JSON.stringify(photoTypes) : undefined,
          files: allNewFiles.length ? allNewFiles : undefined
        }
      });
      setFlash({ type: 'success', message: 'Asset issue updated successfully.' });
      setIsEditing(false);
      setNewBeforePhotos([]);
      setNewAfterPhotos([]);
      setDeletedPhotoIds([]);
      onSaved?.();
      await loadIssue();
    } catch (err) {
      setFlash({
        type: 'error',
        message: getApiErrorMessage(err, 'Failed to update asset issue.')
      });
    }
  });

  return {
    loading,
    issue,
    isEditing,
    setIsEditing,
    issuesConfig,
    criticalityLevels,
    submitId,
    submittedAssetId,
    legacyForm,
    assignmentForm,
    reportIssueDropdownOption,
    reportIssueSingleFields,
    existingBeforePhotos,
    existingAfterPhotos,
    newBeforePhotos,
    setNewBeforePhotos,
    newAfterPhotos,
    setNewAfterPhotos,
    deletedPhotoIds,
    setDeletedPhotoIds,
    handleSave,
    setIssue,
    reload: loadIssue
  };
};
